import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  addDays,
  buildEventDraft,
  DraftDay,
  draftDayHours,
  draftToSchedule,
  time24To12,
  timeTo24,
} from "./eventDraft";
import {
  buildGoogleApplySearchUrl,
  externalApplyLink,
  hasUnconfirmedDates,
  scheduleText,
  toggleTrackerStatus,
  TRACKER_CHOICES,
  trackerStatusLabel,
  UNCONFIRMED_DATES_NOTE,
  unconfirmedDatesLabel,
} from "./eventDiscoveryState";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ApplicationStatus,
  convertEventOpportunity,
  createEventOpportunityReview,
  EventOpportunity,
  getEventOpportunity,
  listEventOpportunityReviews,
  reportEventOpportunity,
  saveEventOpportunity,
  unsaveEventOpportunity,
  updateEventOpportunityNote,
  updateEventOpportunityState,
} from "../../services/eventOpportunitiesApi";

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;
const formatDate = (value?: string, time = false) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        ...(time ? { hour: "numeric", minute: "2-digit" } : {}),
      })
    : "Not provided";
const money = (value?: number, currency = "USD") =>
  value == null
    ? "Not provided"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(value);

function Field({ label, value }: { label: string; value?: string | number }) {
  return (
    <div className="grid gap-1 border-b border-slate-100 py-3 sm:grid-cols-[180px_1fr]">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm font-medium capitalize text-slate-900">
        {value ?? "Not provided"}
      </dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t-8 border-slate-100 px-5 py-6 sm:px-7">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

export default function EventOpportunityDetailPage() {
  const { opportunityId = "" } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<EventOpportunity | null>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [note, setNote] = useState("");
  const [rating, setRating] = useState("5");
  const [reviewText, setReviewText] = useState("");
  const [reportReason, setReportReason] = useState("incorrect-information");
  const [reportDetail, setReportDetail] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [panel, setPanel] = useState<"review" | "report" | "convert" | null>(
    null,
  );
  const [working, setWorking] = useState(false);
  const [days, setDays] = useState<DraftDay[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [detail, reviewItems] = await Promise.all([
        getEventOpportunity(opportunityId),
        listEventOpportunityReviews(opportunityId),
      ]);
      setOpportunity(detail);
      setReviews(reviewItems);
      setNote(detail.userState?.privateNotes || "");
    } catch (loadError) {
      setError(errorMessage(loadError, "Could not load this event."));
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setWorking(true);
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
      await load();
    } catch (actionError) {
      setError(errorMessage(actionError, "The action could not be completed."));
    } finally {
      setWorking(false);
    }
  };

  if (loading && !opportunity) {
    return (
      <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-24 text-center">
        <p className="text-sm font-medium text-slate-600">
          Reviewing source details...
        </p>
      </main>
    );
  }
  if (!opportunity) {
    return (
      <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-24 text-center">
        <h1 className="text-xl font-semibold text-slate-900">
          Event unavailable
        </h1>
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
        <Link
          to="/discover"
          className="mt-5 inline-block font-semibold text-blue-700 hover:underline"
        >
          Back to discovery
        </Link>
      </main>
    );
  }

  const canConvert = ["accepted", "payment-due", "confirmed"].includes(
    opportunity.userState?.status || "",
  );

  const saved = Boolean(opportunity.userState?.saved);
  const draft = buildEventDraft(opportunity);
  const applyLink = externalApplyLink(opportunity);
  const duplicateDates =
    new Set(days.map((day) => day.date)).size !== days.length;
  const totalHours = days.reduce((sum, day) => sum + draftDayHours(day), 0);
  const scheduleProblem = !eventTitle.trim()
    ? "Enter an event title."
    : days.length === 0
      ? "Choose at least one day."
      : days.some((day) => !day.date || draftDayHours(day) <= 0)
        ? "Each day needs a date and an end time after the start time."
        : duplicateDates
          ? "Each day can only be added once."
          : null;
  const openAddToEvents = () => {
    setEventTitle(draft.title);
    setEventLocation(draft.location);
    setDays(draft.days);
    setPanel("convert");
  };
  const updateDay = (index: number, change: Partial<DraftDay>) =>
    setDays((current) =>
      current.map((day, i) => (i === index ? { ...day, ...change } : day)),
    );
  const setStatus = (status: ApplicationStatus) =>
    void run(
      () => updateEventOpportunityState(opportunityId, { status }),
      "Application status updated.",
    );
  const fee =
    opportunity.boothFeeMin != null && opportunity.boothFeeMax != null
      ? `${money(opportunity.boothFeeMin, opportunity.currency)} - ${money(
          opportunity.boothFeeMax,
          opportunity.currency,
        )}`
      : money(
          opportunity.boothFeeMin ?? opportunity.boothFeeMax,
          opportunity.currency,
        );

  const submitReview = async (event: FormEvent) => {
    event.preventDefault();
    setPanel(null);
    await run(
      () =>
        createEventOpportunityReview(opportunityId, {
          overallRating: Number(rating),
          text: reviewText,
          wouldAttendAgain: true,
        }),
      "Review submitted for moderation.",
    );
  };

  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-7 md:px-8">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <header className="px-5 py-6 sm:px-7">
          <Link
            to="/discover"
            className="text-sm font-semibold text-blue-700 hover:underline"
          >
            ← Back to discovery
          </Link>
          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {opportunity.name}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {[opportunity.venueName, opportunity.city, opportunity.state]
                  .filter(Boolean)
                  .join(" · ") || "Location not provided"}
              </p>
              <p className="mt-2 font-semibold text-slate-900">
                {hasUnconfirmedDates(opportunity) ? (
                  <>
                    <span className="text-red-700">
                      {unconfirmedDatesLabel(opportunity)}
                    </span>
                    {scheduleText(opportunity) && (
                      <span className="block font-normal text-slate-700">
                        {scheduleText(opportunity)}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {formatDate(opportunity.startAt, true)}
                    {opportunity.endAt
                      ? ` - ${formatDate(opportunity.endAt, true)}`
                      : ""}
                  </>
                )}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-semibold capitalize text-blue-700">
                  {opportunity.verificationStatus.replaceAll("-", " ")}
                </span>
                <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
                  {opportunity.freshnessStatus.replaceAll("-", " ")}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={working}
                onClick={() =>
                  void run(
                    () =>
                      saved
                        ? unsaveEventOpportunity(opportunityId)
                        : saveEventOpportunity(opportunityId),
                    saved ? "Removed from saved events." : "Event saved.",
                  )
                }
                className="rounded-md border border-blue-600 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
              >
                {saved ? "Saved" : "Save"}
              </button>
              <select
                aria-label="Application status"
                value=""
                onChange={(event) => {
                  const status = event.target.value as ApplicationStatus;
                  if (status)
                    setStatus(
                      toggleTrackerStatus(
                        opportunity.userState?.status,
                        status,
                      ),
                    );
                }}
                className="rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800"
              >
                <option value="">
                  {trackerStatusLabel(opportunity.userState?.status)}
                </option>
                {TRACKER_CHOICES.map((choice) => (
                  <option key={choice.status} value={choice.status}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {notice && (
            <p
              role="status"
              className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"
            >
              {notice}
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-800"
            >
              {error}
            </p>
          )}
        </header>

        <Section title="Apply to this event">
          {hasUnconfirmedDates(opportunity) && (
            <p
              role="note"
              className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm leading-6 text-red-800"
            >
              {UNCONFIRMED_DATES_NOTE}
            </p>
          )}
          <p className="text-sm leading-6 text-slate-600">
            Each organizer takes applications on their own site, and listings
            can be out of date. Apply there, then come back and tell us how it
            went.
          </p>
          <a
            href={applyLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            {applyLink.kind === "organizer"
              ? "Open organizer's site to apply ↗"
              : "Search Google to apply ↗"}
          </a>
          {applyLink.kind === "organizer" && (
            <a
              href={buildGoogleApplySearchUrl(opportunity)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block text-sm font-semibold text-blue-700 hover:underline"
            >
              Link not working? Search Google instead
            </a>
          )}
          <h3 className="mt-6 text-sm font-semibold text-slate-900">
            How is your application going?
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Click your choice again to clear it.
          </p>
          <div
            role="radiogroup"
            aria-label="Application status"
            className="mt-2 grid gap-2 sm:grid-cols-2"
          >
            {TRACKER_CHOICES.map((choice) => {
              const active = choice.status === opportunity.userState?.status;
              return (
                <button
                  key={choice.status}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={working}
                  onClick={() =>
                    setStatus(
                      toggleTrackerStatus(
                        opportunity.userState?.status,
                        choice.status,
                      ),
                    )
                  }
                  className={`rounded-md border px-3 py-2.5 text-left ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}
                >
                  <span className="block text-sm font-semibold text-slate-900">
                    {choice.label}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {choice.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        {opportunity.match && (
          <Section title="Your match">
            <div className="grid gap-6 md:grid-cols-[170px_1fr]">
              <div>
                <p className="text-5xl font-bold text-slate-900">
                  {opportunity.match.score}%
                </p>
                <p className="mt-1 text-sm font-semibold capitalize text-slate-600">
                  {opportunity.match.confidence} confidence
                </p>
              </div>
              <div>
                <p className="text-sm leading-6 text-slate-700">
                  {opportunity.match.primaryReason}
                </p>
                <div className="mt-4 space-y-3">
                  {opportunity.match.components.map((component) => (
                    <div
                      key={component.key}
                      className="grid items-center gap-2 sm:grid-cols-[190px_1fr_48px]"
                    >
                      <span className="text-xs font-medium text-slate-600">
                        {component.label}
                      </span>
                      <div className="h-2 overflow-hidden rounded bg-slate-200">
                        <div
                          className="h-full rounded bg-blue-600"
                          style={{
                            width: `${Math.round((component.points / component.max) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-right text-xs font-semibold text-slate-700">
                        {component.points}/{component.max}
                      </span>
                    </div>
                  ))}
                </div>
                {opportunity.match.negativeFactors.map((factor) => (
                  <p key={factor} className="mt-3 text-sm text-amber-800">
                    Caution: {factor}
                  </p>
                ))}
              </div>
            </div>
          </Section>
        )}

        <Section title="Vendor event">
          <dl>
            <Field
              label="Application status"
              value={opportunity.applicationStatus.replaceAll("-", " ")}
            />
            <Field
              label="Application deadline"
              value={formatDate(opportunity.applicationDeadline)}
            />
            <Field label="Booth fee" value={fee} />
            <Field
              label="Application fee"
              value={money(opportunity.applicationFee, opportunity.currency)}
            />
            <Field
              label="Setting"
              value={opportunity.indoorOutdoor || "Not provided"}
            />
            <Field
              label="Vendor categories"
              value={opportunity.vendorCategories.join(", ") || "Not provided"}
            />
            <Field
              label="Product restrictions"
              value={
                opportunity.productRestrictions?.join(", ") || "Not provided"
              }
            />
            <Field
              label="Expected attendance"
              value={
                opportunity.estimatedAttendance?.toLocaleString() ||
                "Not provided"
              }
            />
          </dl>
          {opportunity.applicationUrl && (
            <a
              href={opportunity.applicationUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Open vendor application ↗
            </a>
          )}
        </Section>

        <Section title="Overview and public contact">
          <p className="text-sm leading-6 text-slate-700">
            {opportunity.summary ||
              opportunity.description ||
              "No overview was provided."}
          </p>
          <dl className="mt-4">
            <Field
              label="Organizer"
              value={opportunity.organizerName || "Not provided"}
            />
            <Field
              label="Address"
              value={
                [
                  opportunity.address,
                  opportunity.city,
                  opportunity.state,
                  opportunity.postalCode,
                ]
                  .filter(Boolean)
                  .join(", ") || "Not provided"
              }
            />
            <Field
              label="Public email"
              value={opportunity.publicEmail || "Not provided"}
            />
            <Field
              label="Public phone"
              value={opportunity.publicPhone || "Not provided"}
            />
          </dl>
          {opportunity.officialWebsite && (
            <a
              href={opportunity.officialWebsite}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block font-semibold text-blue-700 hover:underline"
            >
              Official event website ↗
            </a>
          )}
        </Section>

        <Section title="Sources and freshness">
          <dl>
            <Field
              label="Last checked"
              value={formatDate(opportunity.lastCheckedAt)}
            />
            <Field
              label="Last verified"
              value={formatDate(opportunity.lastVerifiedAt)}
            />
            <Field
              label="Source confidence"
              value={`${Math.round(opportunity.confidenceScore * 100)}%`}
            />
          </dl>
          <div className="mt-4 divide-y divide-slate-100">
            {(opportunity.sources || []).map((source) => (
              <a
                key={source._id}
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between py-3 text-sm hover:text-blue-700"
              >
                <span>
                  <strong>
                    {source.title || new URL(source.url).hostname}
                  </strong>
                  <span className="ml-2 capitalize text-slate-500">
                    {source.sourceType}
                  </span>
                </span>
                <span aria-hidden>↗</span>
              </a>
            ))}
          </div>
          {Boolean(opportunity.missingInformation?.length) && (
            <div className="mt-4 rounded-md bg-amber-50 p-4 text-sm text-amber-900">
              <strong>Verify before applying.</strong> Missing:{" "}
              {opportunity.missingInformation?.join(", ")}.
            </div>
          )}
          <button
            type="button"
            onClick={() => setPanel("report")}
            className="mt-4 text-sm font-semibold text-red-700 hover:underline"
          >
            Report incorrect information
          </button>
        </Section>

        <Section title="Private notes">
          <p className="mb-2 text-xs text-slate-500">
            This note is returned only for your authenticated account.
          </p>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={4}
            placeholder="Organizer follow-up, setup reminders, booth preferences..."
            className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          <button
            type="button"
            disabled={working}
            onClick={() =>
              void run(
                () => updateEventOpportunityNote(opportunityId, note),
                "Private note saved.",
              )
            }
            className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Save note
          </button>
        </Section>

        <Section title="Vendor experiences">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              <strong className="text-xl text-slate-900">
                {opportunity.reviewSummary?.averageRating?.toFixed(1) || "-"}
              </strong>{" "}
              from {opportunity.reviewSummary?.count || 0} public reviews
            </p>
            <button
              type="button"
              onClick={() => setPanel("review")}
              className="rounded-md border border-blue-600 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Share experience
            </button>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {reviews.map((review) => (
              <article key={review._id} className="py-4">
                <div className="flex items-center justify-between text-sm">
                  <strong className="text-slate-900">
                    {review.reviewerLabel}
                  </strong>
                  <span className="font-semibold text-amber-700">
                    {review.overallRating}/5
                  </span>
                </div>
                {review.text && (
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {review.text}
                  </p>
                )}
              </article>
            ))}
          </div>
        </Section>

        <Section title="Add to my events">
          <p className="text-sm leading-6 text-slate-600">
            Once you are in, add it to your events with the day or days you will
            work and your hours. You can change them first.
          </p>
          {!canConvert && !opportunity.userState?.linkedEventId && (
            <p className="mt-3 text-sm text-slate-500">
              Mark your application as accepted to add this event.
            </p>
          )}
          <button
            type="button"
            disabled={
              Boolean(opportunity.userState?.linkedEventId) || !canConvert
            }
            onClick={openAddToEvents}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
          >
            {opportunity.userState?.linkedEventId
              ? "Already in your events"
              : !canConvert
                ? "Acceptance required"
                : "Add to my events"}
          </button>
        </Section>
      </div>

      {panel && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={(event) => {
              if (panel === "review") return void submitReview(event);
              event.preventDefault();
              if (panel === "report") {
                setPanel(null);
                void run(
                  () =>
                    reportEventOpportunity(opportunityId, {
                      reason: reportReason,
                      detail: reportDetail,
                    }),
                  "Report sent for moderation.",
                );
              } else {
                const dailySchedule = draftToSchedule(days);
                if (scheduleProblem || !dailySchedule) return;
                setPanel(null);
                void run(async () => {
                  const result = await convertEventOpportunity(opportunityId, {
                    title: eventTitle.trim(),
                    location: eventLocation.trim(),
                    dailySchedule,
                  });
                  navigate(`/events/${result.event._id}`);
                }, "Added to your events.");
              }
            }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="flex items-start justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                {panel === "review"
                  ? "Share vendor experience"
                  : panel === "report"
                    ? "Report listing"
                    : "Add to my events"}
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setPanel(null)}
                className="text-2xl text-slate-500"
              >
                ×
              </button>
            </div>
            {panel === "review" && (
              <>
                <label className="mt-5 block text-sm font-semibold text-slate-700">
                  Overall rating
                  <select
                    value={rating}
                    onChange={(event) => setRating(event.target.value)}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                  >
                    {[5, 4, 3, 2, 1].map((value) => (
                      <option key={value} value={value}>
                        {value} / 5
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mt-4 block text-sm font-semibold text-slate-700">
                  Experience
                  <textarea
                    value={reviewText}
                    onChange={(event) => setReviewText(event.target.value)}
                    rows={5}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                    placeholder="Communication, setup, traffic, fees, and what vendors should know"
                  />
                </label>
              </>
            )}
            {panel === "report" && (
              <>
                <label className="mt-5 block text-sm font-semibold text-slate-700">
                  Reason
                  <select
                    value={reportReason}
                    onChange={(event) => setReportReason(event.target.value)}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                  >
                    {[
                      "incorrect-information",
                      "duplicate",
                      "application-closed",
                      "cancelled",
                      "invalid-contact",
                      "suspicious",
                    ].map((reason) => (
                      <option key={reason} value={reason}>
                        {reason.replaceAll("-", " ")}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mt-4 block text-sm font-semibold text-slate-700">
                  What should reviewers verify?
                  <textarea
                    value={reportDetail}
                    onChange={(event) => setReportDetail(event.target.value)}
                    rows={4}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                  />
                </label>
              </>
            )}
            {panel === "convert" && (
              <>
                {draft.isSeason && (
                  <p className="mt-3 text-sm text-slate-600">
                    This market runs all season. Add the day or days you will
                    work. You can add more later.
                  </p>
                )}
                {draft.datesEstimated && (
                  <p className="mt-2 text-sm text-red-700">
                    The organizer has not confirmed this season's dates. Check
                    them before you go.
                  </p>
                )}
                {draft.timesEstimated && (
                  <p className="mt-2 text-sm text-red-700">
                    Hours were not listed, so these are placeholders. Set the
                    real times.
                  </p>
                )}
                <label className="mt-5 block text-sm font-semibold text-slate-700">
                  Event title
                  <input
                    required
                    value={eventTitle}
                    onChange={(event) => setEventTitle(event.target.value)}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                  />
                </label>
                <label className="mt-4 block text-sm font-semibold text-slate-700">
                  Location
                  <input
                    value={eventLocation}
                    onChange={(event) => setEventLocation(event.target.value)}
                    className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal"
                  />
                </label>
                <p className="mt-5 text-sm font-semibold text-slate-700">
                  Days and hours
                </p>
                {days.map((day, index) => (
                  <div
                    key={index}
                    className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-slate-200 p-3"
                  >
                    <label className="text-xs font-semibold text-slate-600">
                      Date
                      <input
                        type="date"
                        required
                        value={day.date}
                        onChange={(event) =>
                          updateDay(index, { date: event.target.value })
                        }
                        className="mt-1 block rounded-md border border-slate-300 px-2 py-2 text-sm font-normal"
                      />
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      Start
                      <input
                        type="time"
                        required
                        value={timeTo24(day.startTime)}
                        onChange={(event) =>
                          updateDay(index, {
                            startTime: time24To12(event.target.value),
                          })
                        }
                        className="mt-1 block rounded-md border border-slate-300 px-2 py-2 text-sm font-normal"
                      />
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      End
                      <input
                        type="time"
                        required
                        value={timeTo24(day.endTime)}
                        onChange={(event) =>
                          updateDay(index, {
                            endTime: time24To12(event.target.value),
                          })
                        }
                        className="mt-1 block rounded-md border border-slate-300 px-2 py-2 text-sm font-normal"
                      />
                    </label>
                    {days.length > 1 && (
                      <button
                        type="button"
                        aria-label="Remove this day"
                        onClick={() =>
                          setDays((current) =>
                            current.filter((_, i) => i !== index),
                          )
                        }
                        className="pb-2 text-sm font-semibold text-slate-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <div className="mt-3 flex items-center justify-between">
                  <button
                    type="button"
                    disabled={days.length === 0 || days.length >= 31}
                    onClick={() =>
                      setDays((current) => {
                        const last = current[current.length - 1];
                        return [
                          ...current,
                          {
                            ...last,
                            date: addDays(last.date, draft.isSeason ? 7 : 1),
                          },
                        ];
                      })
                    }
                    className="text-sm font-semibold text-blue-700 hover:underline disabled:text-slate-400"
                  >
                    + Add another day
                  </button>
                  <span className="text-sm text-slate-500">
                    {days.length} {days.length === 1 ? "day" : "days"} ·{" "}
                    {Math.round(totalHours * 10) / 10} hours
                  </span>
                </div>
                {scheduleProblem && (
                  <p role="alert" className="mt-2 text-sm text-red-700">
                    {scheduleProblem}
                  </p>
                )}
              </>
            )}
            <button
              type="submit"
              disabled={
                working || (panel === "convert" && Boolean(scheduleProblem))
              }
              className={`mt-5 w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-400 ${panel === "report" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
            >
              {panel === "review"
                ? "Submit for moderation"
                : panel === "report"
                  ? "Send report"
                  : "Create event"}
            </button>
          </form>
        </div>
      )}
    </main>
  );
}
